import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import DisbursedMenu from '../Disbursed/MenuButton';
import {
  convertToMexicoCentralTime,
  formatPhoneNumber,
  formattedDate,
  FormattedDateEnum,
} from 'utils';
import { PendingTypeEnum } from '../../../types';
import { Button, Tooltip } from '@mui/material';
import { CelebrationBadge } from 'components/common/CelebrationBadge';

const formatDate = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString('es-MX', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City',
  });
};

export const columnsInReview = ({
  handleClickVerifyButton,
  handleClickChanguePayment,
  promoterData,
}: {
  handleClickVerifyButton: (id: string) => void;
  handleClickChanguePayment: (id: string) => void;
  promoterData: Record<string, any>;
}) => {
  const pendingColumns: GridColDef[] = [
    {
      field: 'renewed',
      headerName: 'Renovado',
      flex: 1,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { previousCreditId } = row;
        const badgeText = previousCreditId ? 'Renovado' : 'Nuevo';

        return <CelebrationBadge text={badgeText} />;
      },
    },
    {
      field: 'clientName',
      headerName: 'Nombre',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { clientName } = row;
        return <div>{clientName}</div>;
      },
    },
    {
      field: 'phone',
      headerName: 'Teléfono',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { phone } = row;
        return <div>{formatPhoneNumber(phone)}</div>;
      },
    },
    {
      field: 'promoterFullName',
      headerName: 'Promotor',
      flex: 1,
      renderCell: (params) => {
        const promoter = promoterData[params.row.createdBy];
        const percentage = promoter?.percentage || 'N/A';
        const promoterName = params.row.promoterFullName;

        return (
          <Tooltip title={`Porcentaje: ${percentage}`} arrow>
            <span>{promoterName}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 220,
      valueFormatter: (params) => {
        if (!params.value) return ''; // Maneja valores nulos o indefinidos

        const date = new Date(params.value); // Convierte el valor a una fecha
        const options: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Usa formato de 24 horas
          timeZone: 'America/Mexico_City', // Zona horaria de México
        };

        return new Intl.DateTimeFormat('es-MX', options).format(date); // Formatea la fecha
      },
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'dispersionAmount',
      headerName: 'Dispersión',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      renderCell: ({ row }: GridRenderCellParams) => (
        <Button variant="outlined" onClick={() => handleClickVerifyButton(row.id)}>
          Verificar
        </Button>
      ),
    },
  ];

  return pendingColumns;
};

export const columnsPending = ({
  handleClickChanguePayment,
}: {
  handleClickChanguePayment: (id: string) => void;
}) => {
  const pendingColumns: GridColDef[] = [
    {
      field: 'clientName',
      headerName: 'Nombre',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { clientName } = row;
        return <div>{clientName}</div>;
      },
    },
    {
      field: 'phone',
      headerName: 'Teléfono',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { phone } = row;
        return <div>{formatPhoneNumber(phone)}</div>;
      },
    },
    {
      field: 'promoterFullName',
      headerName: 'Promotor(a)',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { promoterFullName } = row;
        return promoterFullName || '';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 220,
      valueFormatter: (params) => {
        if (!params.value) return ''; // Maneja valores nulos o indefinidos

        const date = new Date(params.value); // Convierte el valor a una fecha
        const options: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Usa formato de 24 horas
          timeZone: 'America/Mexico_City', // Zona horaria de México
        };

        return new Intl.DateTimeFormat('es-MX', options).format(date); // Formatea la fecha
      },
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'changuePayment',
      headerName: '',
      type: 'changuePayment',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      renderCell: ({ row }: GridRenderCellParams) => (
        <Button variant="outlined" onClick={() => handleClickChanguePayment(row.id)}>
          Cambiar pago
        </Button>
      ),
    },
  ];

  return pendingColumns;
};

export const columnsDisbursed = (refetch: () => void, changeTab: (tab: number) => void) => {
  const columnsDisbursed: GridColDef[] = [
    {
      field: 'renewed',
      headerName: 'Renovado',
      flex: 1,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { previousCreditId } = row;
        const badgeText = previousCreditId ? 'Renovado' : 'Nuevo';

        return <CelebrationBadge text={badgeText} />;
      },
    },
    {
      field: 'clientFullName',
      headerName: 'Nombre',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { clientFullName, clientName } = row;
        return `${clientFullName || clientName}`;
      },
    },
    {
      field: 'phoneNumber',
      headerName: 'Teléfono',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { clientPhoneNumber: paymentPhone, clientPhoneLada, phone: creditPhone } = row;
        const clientPhone = creditPhone || `${clientPhoneLada} ${formatPhoneNumber(paymentPhone)}`;
        return clientPhone;
      },
    },
    {
      field: 'promoterFullName',
      headerName: 'Promotor(a)',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { promoterFullName } = row;
        return promoterFullName || '';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 220,
      valueFormatter: (params) => {
        if (!params.value) return ''; // Maneja valores nulos o indefinidos

        const date = new Date(params.value); // Convierte el valor a una fecha
        const options: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Usa formato de 24 horas
          timeZone: 'America/Mexico_City', // Zona horaria de México
        };

        return new Intl.DateTimeFormat('es-MX', options).format(date); // Formatea la fecha
      },
    },
    {
      // TODO: change this field when the backend is ready
      field: 'updatedAt',
      headerName: 'Fecha de Desembolso',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const mexicoCentralDate = convertToMexicoCentralTime(value as string);
        const date = formattedDate(mexicoCentralDate.toString(), FormattedDateEnum.FULL);
        return date;
      },
    },
    {
      field: 'referenceNumber',
      headerName: 'No. de Referencia',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { referenceNumber, speiNumber } = row;
        return referenceNumber || speiNumber;
      },
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'dispersionAmount',
      headerName: 'Dispersión',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      getActions: ({ row }: GridRowParams) => [
        <DisbursedMenu
          key={row.id}
          payoutID={row.id}
          clientFullName={row.clientFullName}
          amount={row.amount}
          refetch={refetch}
          changeTab={changeTab}
          paymentDate={row.disbursementDate}
        />,
      ],
    },
  ];

  return columnsDisbursed;
};

export const columnsAccepted = ({
  handleClickDisburseButton,
}: {
  handleClickDisburseButton: (data: { amount: number; id: string; clientFullName: string }) => void;
}) => {
  const columns: GridColDef[] = [
    {
      field: 'renewed',
      headerName: 'Renovado',
      flex: 1,
      filterable: false,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { previousCreditId } = row;
        const badgeText = previousCreditId ? 'Renovado' : 'Nuevo';

        return <CelebrationBadge text={badgeText} />;
      },
    },
    {
      field: 'clientFullName',
      headerName: 'Nombre',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { clientFullName } = row;
        return `${clientFullName}`;
      },
    },
    {
      field: 'clientPhoneNumber',
      headerName: 'Teléfono',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { clientPhoneNumber, clientPhoneLada } = row;
        return `${clientPhoneLada} ${formatPhoneNumber(clientPhoneNumber)}`;
      },
    },
    {
      field: 'promoterFullName',
      headerName: 'Promotor(a)',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { promoterFullName } = row;
        return promoterFullName || '';
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 220,
      valueFormatter: (params) => {
        if (!params.value) return ''; // Maneja valores nulos o indefinidos

        const date = new Date(params.value); // Convierte el valor a una fecha
        const options: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false, // Usa formato de 24 horas
          timeZone: 'America/Mexico_City', // Zona horaria de México
        };

        return new Intl.DateTimeFormat('es-MX', options).format(date); // Formatea la fecha
      },
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'dispersionAmount',
      headerName: 'Dispersión',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      renderCell: ({ row }: GridRenderCellParams) => {
        return (
          <Button
            variant="outlined"
            onClick={() =>
              handleClickDisburseButton({
                id: row.id,
                amount: row.amount,
                clientFullName: row.clientFullName,
              })
            }
          >
            Desembolsar
          </Button>
        );
      },
    },
  ];

  return columns;
};

export const columnsRejected = () => {
  const columns: GridColDef[] = [
    // {
    //   field: 'status',
    //   flex: 1,
    //   renderCell: ({ row }: GridRenderCellParams) => {
    //     const { clientName } = row
    //     return `${clientName}`
    //   },
    // },
    {
      field: 'clientName',
      headerName: 'Nombre',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { clientName } = row;
        return `${clientName}`;
      },
    },
    {
      field: 'phone',
      headerName: 'Teléfono',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { phone } = row;
        return <div>{formatPhoneNumber(phone)}</div>;
      },
    },
    {
      field: 'promoterFullName',
      headerName: 'Promotor(a)',
      flex: 1,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { promoterFullName } = row;
        return promoterFullName || '';
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Última actualización',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const mexicoCentralDate = convertToMexicoCentralTime(value as string);
        const date = formattedDate(mexicoCentralDate.toString(), FormattedDateEnum.FULL);
        return date;
      },
    },
    {
      field: 'amount',
      headerName: 'Monto',
      flex: 1,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        const amount = Number(value);
        const formatted = amount.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
        });
        return formatted + ' MXN';
      },
    },
    {
      field: 'rejectionReason',
      headerName: 'Razon de rechazo',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => {
        const { rejectionReason } = row;
        const maxLength = 50; // Longitud máxima antes de mostrar puntos suspensivos

        if (!rejectionReason) {
          return <div>Error al dispersar centavo.</div>;
        }

        const displayText =
          rejectionReason.length > maxLength
            ? `${rejectionReason.substring(0, maxLength)}...`
            : rejectionReason;

        return (
          <div
            title={rejectionReason} // Tooltip nativo del navegador
            style={{
              cursor: rejectionReason.length > maxLength ? 'help' : 'default',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayText}
          </div>
        );
      },
    },
  ];
  return columns;
};
